import React, {useEffect, useMemo, useState} from 'react';
import {gql, useLazyQuery} from '@apollo/client';
import {GridColDef, useGridApiRef} from '@mui/x-data-grid-pro';
import {capitalize, rest} from 'lodash-es';
import {Box} from '@mui/material';
import {Link, useNavigate} from 'react-router-dom';
import {Email} from '@mui/icons-material';

import {contactTypeColors} from '../../components/ContactPage/contactStyles';
import {RenderColor} from '../../components/EditorGrid/GridRenderComponents';
import {ACCOUNT_SEARCH_FRAGMENT} from '../../graphql/fragments';
import {Panel} from '../../components/Panel/Panel';
import {darkBlue, themeColors} from '../../theme/Theme';
import Loading from '../../components/Loading/Loading';
import {LeadsAndAccounts_leadsAndAccounts} from '../../schema-types/LeadsAndAccounts';
import {
  AlphabeticalAccounts,
  AlphabeticalAccountsVariables,
} from '../../schema-types/AlphabeticalAccounts';
import {useDjangoContext} from '../../contexts/useAuthContext';
import {AccountSearchFragment} from '../../schema-types/AccountSearchFragment';
import {StickyDataGrid} from '../../components/Common/StickyDataGrid';
import {gridCellDef} from '../../utils/GridCellDef';

const classes = {
  disableNext: {
    '& button[aria-label="Next page"]': {
      pointerEvents: 'none',
      opacity: 0.4,
    },
  },

  grid: {
    '& .last': {},
  },

  letterWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: `1px solid ${themeColors.tan}`,
    borderRadius: 20,
    width: 30,
    height: 30,
    cursor: 'pointer',
    padding: 0,
    '&:hover': {
      borderColor: themeColors.backgroundMuted,
    },
  },

  activeWrapper: {
    border: 'none',
    backgroundColor: themeColors.backgroundMuted,
    color: themeColors.white,
    fontWeight: 'bold',
  },
};

type letterProps = {
  setStartLetter: React.Dispatch<React.SetStateAction<string>>;
  startLetter: string;
  loading: boolean;
};

export const LetterButton: React.FC<letterProps & {letter: string}> = ({
  letter,
  setStartLetter,
  startLetter,
  loading,
}) => {
  return (
    <button
      type="button"
      disabled={loading}
      style={{
        ...classes.letterWrapper,
        ...(startLetter === letter ? classes.activeWrapper : {}),
      }}
      onClick={() => (loading ? null : setStartLetter(letter))}
    >
      {letter}
    </button>
  );
};

export const AlphabeticalPagination: React.FC<letterProps> = (props) => {
  return (
    <Box display="flex" justifyContent="space-around" width="100%" alignItems="center">
      <button
        type="button"
        style={classes.letterWrapper}
        onClick={() => {
          const previousLetter = alphabet[alphabet.indexOf(props.startLetter) - 1];
          const hasPrevious = !!previousLetter;

          props.setStartLetter(hasPrevious ? previousLetter : 'Z');
        }}
      >
        ⬅
      </button>
      {alphabet.map((letter) => {
        return <LetterButton key={letter} letter={letter} {...props} />;
      })}
      <button
        type="button"
        style={classes.letterWrapper}
        onClick={() => {
          const nextLetter = alphabet[alphabet.indexOf(props.startLetter) + 1];
          const hasNext = !!nextLetter;

          props.setStartLetter(hasNext ? nextLetter : 'A');
        }}
      >
        ➡
      </button>
    </Box>
  );
};

export const AlphabeticalAccountsWidget = () => {
  const {businessUnit} = useDjangoContext();
  const [loading, setLoading] = useState(false);
  const [startLetter, setStartLetter] = useState('A');
  const [fetch, {data, loading: initialLoading}] = useLazyQuery<
    AlphabeticalAccounts,
    AlphabeticalAccountsVariables
  >(
    gql`
      query AlphabeticalAccounts(
        $afterId: String
        $staffId: UUID
        $before: String
        $startLetter: String
      ) {
        dashboardAccounts(
          staffId: $staffId
          after: $afterId
          before: $before
          first: 100
          startLetter: $startLetter
        ) {
          pageInfo {
            hasNextPage
            endCursor
            hasPreviousPage
            startCursor
          }
          totalCount
          edges {
            cursor
            node {
              ...AccountSearchFragment
            }
          }
        }
      }
      ${ACCOUNT_SEARCH_FRAGMENT}
    `,
    {
      variables: {
        startLetter,
      },
    }
  );

  const apiRef = useGridApiRef();

  useEffect(() => {
    setLoading(true);
    fetch({
      variables: {
        startLetter,
      },
    }).finally(() => {
      setLoading(false);
      apiRef.current?.scrollToIndexes({rowIndex: 0});
    });
  }, [startLetter, apiRef.current]);
  const navigate = useNavigate();

  const items = useMemo<AccountSearchFragment[]>(() => {
    const nodes: AccountSearchFragment[] =
      data?.dashboardAccounts?.edges.map((x) => x!.node!) || [];
    return [...nodes];
  }, [data?.dashboardAccounts]);

  if (initialLoading) {
    return <Loading entityName="accounts" />;
  }

  const totalCount = data?.dashboardAccounts?.totalCount || 0;

  return (
    <Panel color={themeColors.blue} title={`${businessUnit.name} Accounts`}>
      <Box mt={2} width="100%" />
      <Box mt={2} />
      <Box sx={{...(loading ? classes.disableNext : {})}} display="flex" minHeight={700}>
        <Box flexGrow={1}>
          <StickyDataGrid
            rowCount={totalCount}
            columns={columns}
            isCellEditable={() => false}
            rows={items.filter((item) => Boolean(item.accountStatus))}
            pagination
            slots={{
              pagination: (params) => {
                return (
                  <AlphabeticalPagination
                    loading={loading}
                    setStartLetter={setStartLetter}
                    startLetter={startLetter}
                  />
                );
              },
            }}
            sx={classes.grid}
            getRowClassName={(params) => (params.isLastVisible ? 'last' : '')}
            apiRef={apiRef} // used for scrolling
            rowHeight={50}
            onRowDoubleClick={(params) => {
              const type = params.row.__typename;
              if (type === 'LeadType') {
                navigate({pathname: `/leads/${params.row.id}/basic`});
              } else {
                navigate({pathname: `/account/${params.row.id}`});
              }
            }}
            {...rest}
          />
        </Box>
      </Box>
    </Panel>
  );
};

type AccountColumnProps = GridColDef & {
  field: string;
};

export const RenderAccountNameAndEmail: gridCellDef<LeadsAndAccounts_leadsAndAccounts> = (
  params
) => {
  const account = params.row;
  const email =
    // eslint-disable-next-line no-underscore-dangle
    account.__typename === 'AccountType' ? account.primaryContact?.email : null;
  return (
    <div
      style={{
        flex: 1,
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4,
        color: darkBlue,
        textDecoration: 'none',
        lineHeight: 1,
        height: '100%',
      }}
    >
      <Link
        role="a"
        to={{
          pathname:
            // eslint-disable-next-line no-underscore-dangle
            account.__typename === 'LeadType'
              ? `/leads/${params.id}/basic`
              : `/account/${params.id}`,
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
        style={{fontWeight: 600, color: darkBlue, textDecoration: 'none'}}
      >
        <>{params.row.name || ''} </>
      </Link>
      {params.row.isDeleted ? '(inactive)' : ''}
      {email ? (
        <a
          className="sentry-mask"
          onClick={(e) => e.stopPropagation()} // disable row navigation event
          style={{
            color: darkBlue,
            textAlign: 'center',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
          }}
          href={`mailto:${email}`}
        >
          <Email style={{marginRight: 4}} fontSize="small" />
          {email}
        </a>
      ) : null}
    </div>
  );
};

const columns: AccountColumnProps[] = [
  {
    field: 'name',
    headerName: ' ',
    flex: 2,
    renderCell: RenderAccountNameAndEmail,
  },
  {
    field: 'accountStatus',
    headerName: 'Status',
    flex: 1,
    valueGetter: (value, row, column, apiRef) => row.accountStatus || 'Lead',
    renderCell: RenderColor(contactTypeColors),
  },
  {
    field: 'city',
    headerName: 'City',
    valueGetter: (value, row, column, apiRef) =>
      row.city || (row.addresses?.length ? capitalize(row.addresses[0].city) : null),
    flex: 1,
  },
  {
    field: 'adviser.name',
    headerName: 'Adviser',
    flex: 1,
    valueGetter: (value, row, column, apiRef) => row.adviser?.name,
  },
  {
    field: 'primaryContact' as any,
    headerName: 'Primary',
    valueGetter: (value, row, column, apiRef) => row.primaryContact?.name,
    flex: 1,
  },
  {
    field: 'secondaryContact' as any,
    headerName: 'Secondary',
    valueGetter: (value, row, column, apiRef) => row.secondaryContact?.name,
    flex: 1,
  },
  {
    field: 'primaryPhone',
    headerName: 'Phone',
    valueGetter: (value, row, column, apiRef) =>
      row.primaryPhone || row.homePhone || row.mobilePhone,
    flex: 1,
  },
  {
    field: 'plans',
    headerName: 'Account value',
    flex: 1,
    valueGetter: (value, row, column, apiRef) => {
      return `£${row.plans}`;
    },
  },
];

const alphabet = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
];
